/**
 * Shared builder for the "Raw JSON" view of a document — the exact object shown
 * in the DocumentView Raw JSON tab and downloaded by the folder "Download JSON"
 * action. Keeping this in one place guarantees both stay byte-for-byte identical.
 */
import { getAllPagesData } from "./Api";
import { transformToDigitalFormat } from "./transformData";

// Match the DocumentView fetch tuning: pages-data caps `limit` at 200 server-side.
const PAGE_BATCH_SIZE = 25;
const PAGE_FETCH_CONCURRENCY = 4;

export interface RawJsonPage {
  page_number: number;
  page_id: string;
  raw_data: any;
  readable_data: any;
}

/**
 * Build the object rendered in the Raw JSON tab. `raw_data` is the authoritative
 * source for each page's transformed digital content.
 */
export const buildRawJsonObject = (documentName: string, pages: RawJsonPage[]) => ({
  document_name: documentName,
  total_pages: pages.length,
  pages: pages.map((p) => ({
    page_number: p.page_number,
    ...transformToDigitalFormat(p.readable_data, p.raw_data),
  })),
});

/**
 * Fetch every page (including raw_data) for a document, batched with bounded
 * concurrency. `pageCount` should be the document's known page count.
 */
export const fetchAllDocumentPages = async (
  documentId: string,
  pageCount: number,
): Promise<RawJsonPage[]> => {
  const offsets: number[] = [];
  for (let off = 0; off < pageCount; off += PAGE_BATCH_SIZE) offsets.push(off);

  const batches: ({ id: string; raw_data: any; readable_data: any }[] | undefined)[] =
    new Array(offsets.length);

  let nextBatchIdx = 0;
  const worker = async () => {
    while (true) {
      const idx = nextBatchIdx++;
      if (idx >= offsets.length) return;
      const { data } = await getAllPagesData(documentId, {
        includeRaw: true,
        offset: offsets[idx]!,
        limit: PAGE_BATCH_SIZE,
      });
      batches[idx] = data;
    }
  };

  await Promise.all(
    Array.from({ length: PAGE_FETCH_CONCURRENCY }, () => worker()),
  );

  return batches
    .flatMap((b) => b ?? [])
    .map((p, index) => ({
      page_number: index + 1,
      page_id: p.id,
      raw_data: p.raw_data,
      readable_data: p.readable_data,
    }));
};

/** Fetch a document and return its pretty-printed Raw JSON string. */
export const fetchDocumentRawJsonString = async (
  documentId: string,
  documentName: string,
  pageCount: number,
): Promise<string> => {
  const pages = await fetchAllDocumentPages(documentId, pageCount);
  return JSON.stringify(buildRawJsonObject(documentName, pages), null, 2);
};
